import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getUserToken } from "../../libs/user";
import { getLinkToken, getProfileService } from "../../services/auth";
import { startLoader, stopLoader } from "./appSlice";
import {
  cricbuzzResendOtpService,
  cricbuzzSendOtpService,
  cricbuzzVerifyOtpService,
} from "../../services/remittance";

const initialState = {
  bridgeData: null,
  nbStatusInfo: null,
  back: false,
  isBackBtnEnabled: false,
  isLogout: false,
  sendOtpData: {
    data: [],
    error: null,
    status: null,
  },
  verifyOtpData: {
    data: [],
    error: null,
    status: null,
  },
  resendOtpData: {
    data: [],
    error: null,
    status: null,
  },
};

export const fetchUserDetails = createAsyncThunk(
  "remittance/fetchUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const token = getUserToken();
      const response = await getProfileService(token);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchLinkToken = createAsyncThunk(
  "remittance/fetchLinkToken",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    const { shouldFetchUserDetails = false, navigate, onBlockingError } =
      payload || {};
    try {
      const state = getState();
      const { fingerprint, remittance } = state || {};
      if (remittance.bridgeData) {
        // If something earlier left loader on, don't keep showing it.
        dispatch(stopLoader());
        return;
      }
      dispatch(startLoader());
      const token = getUserToken();

      // Make parallel calls if fetchUserDetails flag is true
      const linkTokenPromise = getLinkToken(token, fingerprint.data);
      const userDetailsPromise = shouldFetchUserDetails
        ? dispatch(fetchUserDetails())
            .unwrap()
            .catch((err) => {
              console.warn("Failed to fetch user details:", err);
              return null;
            })
        : Promise.resolve(null);

      const [linkTokenResponse, userDetailsResponse] = await Promise.allSettled(
        [linkTokenPromise, userDetailsPromise]
      );

      // Check user details first to determine if we should stop execution
      const { status, value } = userDetailsResponse || {};
      const channel = window.sessionStorage.getItem("channel");

      // Stop further execution if email_updated is false and channel is TOI
      if (navigate && status === "fulfilled" && value) {
        const email_updated = value?.user?.email_updated;
        if (!email_updated && channel === "TOI") {
          // Clean up and navigate - this stops all further execution
          dispatch(deleteBridgeData());
          dispatch(stopLoader());
          navigate("/onboarding/email-update", { replace: true });
          // Return a rejected promise to prevent the fulfilled case from being triggered
          // This ensures linkToken response is not saved to state
          return rejectWithValue({
            code: "EMAIL_NOT_UPDATED",
            message: "Email not updated - redirecting to email update",
            redirect: true,
          });
        }
      }

      // Handle link token response - this is the primary response
      // This code will only execute if email_updated is true or channel is not TOI
      if (
        linkTokenResponse.status === "fulfilled" &&
        linkTokenResponse.value?.success &&
        ((userDetailsResponse.status === "fulfilled" &&
          userDetailsResponse.value?.user?.email_updated) ||
          !shouldFetchUserDetails)
      ) {
        dispatch(stopLoader());
        // User details are automatically saved to authSlice via extraReducer
        // Return linkToken response (maintain backward compatibility)
        return linkTokenResponse.value;
      } else if (linkTokenResponse.status === "rejected") {
        const errData =
          linkTokenResponse.reason?.response?.data || linkTokenResponse.reason;
        const status =
          linkTokenResponse.reason?.response?.status ||
          errData?.error?.status_code;
        const code = errData?.error?.code;

        // Handle multi-device login error (status 400)
        if (status === 400) {
          const payload = {
            code,
            status,
            message:
              errData?.error?.message ||
              "You are currently logged in on another device. Please log out of other device(s) and try again.",
          };
          dispatch(stopLoader());
          if (onBlockingError) {
            onBlockingError(payload);
          }
          return rejectWithValue(payload);
        }

        dispatch(stopLoader());
        return rejectWithValue(errData);
      }
      const nextPath =
        channel === "TOI" ? "/onboarding/email-update" : "/signup";
      dispatch(deleteBridgeData());
      dispatch(stopLoader());
      navigate(nextPath, { replace: true });
      return rejectWithValue(null);
    } catch (error) {
      const errData = error.response?.data || error;
      const status = error.response?.status || errData?.error?.status_code;
      const code = errData?.error?.code;

      if (status === 400) {
        dispatch(stopLoader());
        const payload = {
          code,
          status,
          message:
            errData?.error?.message ||
            "You are currently logged in on another device. Please log out of other device(s) and try again.",
        };
        if (onBlockingError) {
          onBlockingError(payload);
        }
        return rejectWithValue(payload);
      }

      dispatch(stopLoader());
      return rejectWithValue(errData);
    }
  }
);

export const cricbuzzSendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (obj, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoader());
      const { id, token, action_type, mobile_number } = obj;
      const response = await cricbuzzSendOtpService(
        id,
        token,
        action_type,
        mobile_number
      );
      dispatch(stopLoader());
      return response;
    } catch (error) {
      dispatch(stopLoader());
      return rejectWithValue(error.response.data);
    }
  }
);

export const cricbuzzVerifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (obj, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoader());
      const { message_id, mobile_number, otp } = obj;
      const response = await cricbuzzVerifyOtpService(
        message_id,
        mobile_number,
        otp
      );
      dispatch(stopLoader());
      return response;
    } catch (error) {
      dispatch(stopLoader());
      return rejectWithValue(error.response.data);
    }
  }
);

export const cricbuzzResendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (obj, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoader());
      const { message_id, mobile_number } = obj;
      const response = await cricbuzzResendOtpService(
        message_id,
        mobile_number
      );
      dispatch(stopLoader());
      return response;
    } catch (error) {
      dispatch(stopLoader());
      return rejectWithValue(error.response.data);
    }
  }
);

export const remittanceSlice = createSlice({
  name: "remittance",
  initialState: initialState,
  reducers: {
    updateBridgeData: (state, action) => {
      if (state.bridgeData) {
        state.bridgeData = {
          ...state.bridgeData,
          ...action.payload,
        };
        return;
      }
      state.bridgeData = {
        ...action.payload,
      };
    },
    deleteBridgeData: (state) => {
      state.bridgeData = null;
    },
    updateNbStatusInfo: (state, action) => {
      state.nbStatusInfo = { ...action.payload };
    },
    goBack: (state, action) => {
      state.back = action.payload;
    },
    toggleBackBtnVisiblity: (state, action) => {
      state.isBackBtnEnabled = action.payload;
    },
    setLogout: (state, action) => {
      state.isLogout = action.payload;
    },
    reset: (state) => {
      state = initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLinkToken.fulfilled, (state, action) => {
        // Handle the response - maintain backward compatibility
        state.bridgeData = { ...action.payload, success: true };
      })
      .addCase(fetchLinkToken.rejected, (state, action) => {
        state.bridgeData = { ...action.payload, success: false };
      })
      // .addCase(fetchUserDetails.pending, (state) => {
      //   // Optional: track loading state if needed
      // })
      // .addCase(fetchUserDetails.fulfilled, (state) => {
      //   // User details are handled in authSlice
      // })
      // .addCase(fetchUserDetails.rejected, (state) => {
      //   // Optional: track error state if needed
      // })

      .addCase(cricbuzzSendOtp.pending, (state, action) => {
        state.sendOtpData = { ...state.sendOtpData, status: "loading" };
      })
      .addCase(cricbuzzSendOtp.fulfilled, (state, action) => {
        state.sendOtpData = {
          ...state.sendOtpData,
          status: "succeeded",
          data: action.payload,
          error: null,
        };
      })
      .addCase(cricbuzzSendOtp.rejected, (state, action) => {
        state.sendOtpData = {
          ...state.sendOtpData,
          status: "failed",
          error: action.payload,
          data: [],
        };
      })

      .addCase(cricbuzzVerifyOtp.pending, (state, action) => {
        state.verifyOtpData = { ...state.verifyOtpData, status: "loading" };
      })
      .addCase(cricbuzzVerifyOtp.fulfilled, (state, action) => {
        state.verifyOtpData = {
          ...state.verifyOtpData,
          status: "succeeded",
          data: action.payload,
          error: null,
        };
      })
      .addCase(cricbuzzVerifyOtp.rejected, (state, action) => {
        state.verifyOtpData = {
          ...state.verifyOtpData,
          status: "failed",
          error: action.payload,
          data: [],
        };
      })
      .addCase(cricbuzzResendOtp.pending, (state, action) => {
        state.resendOtpData = { ...state.resendOtpData, status: "loading" };
      })
      .addCase(cricbuzzResendOtp.fulfilled, (state, action) => {
        state.resendOtpData = {
          ...state.resendOtpData,
          status: "succeeded",
          data: action.payload,
          error: null,
        };
      })
      .addCase(cricbuzzResendOtp.rejected, (state, action) => {
        state.resendOtpData = {
          ...state.resendOtpData,
          status: "failed",
          error: action.payload,
          data: [],
        };
      });
  },
});

export const {
  updateBridgeData,
  deleteBridgeData,
  updateNbStatusInfo,
  goBack,
  toggleBackBtnVisiblity,
  setLogout,
  reset,
} = remittanceSlice.actions;

export default remittanceSlice.reducer;
